import { type FlowReRunVersionTarget } from 'flow-schema';

import type { Common } from '../common.js';
import {
  type CancelBulkQuery,
  type CancelBulkResponse,
  type ExecuteBulkQueryParams,
  type ExecuteBulkResponse,
  type ExecuteFlowDefinitionQueryParams,
  type ExecuteFlowResponse,
  type ReRunBulkQuery,
  type ReRunBulkResponse,
  type SignalFlowBody,
  type WorkflowExecutionDescription,
} from '../models/flow/flowExecution.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flow-executions';

  constructor(common: Common) {
    this.common = common;
  }

  async executeByFlowDefinitionId(
    params: ExecuteFlowDefinitionQueryParams
  ): Promise<ExecuteFlowResponse> {
    const { flowDefinitionId, practiceId, entityId, entityType, type, blockMocks } = params;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/execute-definition/${flowDefinitionId}`,
      { practiceId, entityId, entityType, type }
    );
    return await this.common.postJSON(url, { blockMocks });
  }

  async executeBulk(body: ExecuteBulkQueryParams): Promise<ExecuteBulkResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/execute-bulk`);
    return await this.common.postJSON(url, body);
  }

  async reRunByFlowRunId(
    flowRunId: string,
    versionTarget?: FlowReRunVersionTarget
  ): Promise<ExecuteFlowResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/re-run/${flowRunId}`, {
      versionTarget,
    });
    return await this.common.postJSON(url, {});
  }

  async resumeByFlowRunIdAndBlockId(args: {
    flowRunId: string;
    blockId: string;
    versionTarget?: FlowReRunVersionTarget;
  }): Promise<ExecuteFlowResponse> {
    const { flowRunId, blockId, versionTarget } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/resume/${flowRunId}`);
    return await this.common.postJSON(url, { versionTarget, blockId });
  }

  async signalFlow(args: SignalFlowBody): Promise<void> {
    const { workflowId, signalName, payload } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/signal`);
    return await this.common.postJSON(url, {
      workflowId,
      signalName,
      payload,
    });
  }

  async getFlowRunStatus(flowRunId: string): Promise<WorkflowExecutionDescription> {
    const url = this.common.constructPath(`${this.baseEndpoint}/status/${flowRunId}`);
    return await this.common.getJSON(url);
  }

  async cancelByFlowRunId(flowRunId: string): Promise<{ cancelSubmitted: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/cancel/${flowRunId}`);
    return await this.common.postJSON(url, {});
  }

  async cancelBulkByQuery(query: CancelBulkQuery): Promise<CancelBulkResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/cancel-bulk`);
    return await this.common.postJSON(url, query);
  }

  async reRunBulkByQuery(query: ReRunBulkQuery): Promise<ReRunBulkResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/re-run-bulk`);
    return await this.common.postJSON(url, query);
  }
}
